import _ from 'lodash';
import AppRestService from './app-rest.service';
import MovieModel from '../models/movie.model';


const _imdbUrl = 'https://www.imdb.com';
const _youtubeUrl = 'https://www.youtube.com';

const _mpaaRatingOptions = [
  { label: 'G',       value: 'G' },
  { label: 'PG',      value: 'PG' },
  { label: 'PG-13',   value: 'PG-13' },
  { label: 'R',       value: 'R' },
  { label: 'NC-17',   value: 'NC-17' },
  { label: 'Unrated', value: 'UNRATED' },
  { label: 'Unknown', value: 'UNKNOWN' },
  { label: '',        value: null },
];

const _mpaaRatings = _.map(_mpaaRatingOptions, 'value');


/**
 * Service Endpoint Implementation
 */
export default class MoviesService extends AppRestService {

  static get MPAARatingOptions() {
    return _mpaaRatingOptions;
  }

  constructor(opts = null) {
    super(opts);
    this.serviceName = 'movies';
    this.routePath = 'movies';
    this.idParamName = 'movie_id';
    this.modelClass = MovieModel;
    this.authEnabled = true;

    this.imdbUrl = _imdbUrl;
    this.youtubeUrl = _youtubeUrl;
  }

  getPosterUrlFromMovie(movie, params = null) {
    let url = null;
    if (movie) {
      url = this.getPosterUrlFromMovieId(movie.id, movie.posterMediaIdIntValue, params);
    }
    return url;
  }

  // NOTE: poster id is not really used, but it is a nice
  // cachebuster for when the poster image gets changed after an upload
  getPosterUrlFromMovieId(movieId, posterMediaId = 0, params = null) {
    let url = null;
    if (!movieId) { movieId = 0; }
    if (!posterMediaId) { posterMediaId = 0; }
    if (movieId > 0 || posterMediaId > 0) {
      let queryParams = {};
      if (movieId > 0) {
        queryParams.movie_id = movieId;
      }
      if (posterMediaId > 0) {
        queryParams.poster_media_id = posterMediaId;
      }
      if (!_.isEmpty(params)) {
        _.extend(queryParams, params);
      }
      let path = [this.routePath, '/poster/view'];
      url = this.request.buildUrl(path, queryParams);
    }
    return url;
  }

  getPosterUploadUrl(item) {
    let queryParams = { movie_id: item.id };
    let authToken = _.get(this.request, 'config.authToken') || null;
    if (authToken) {
      queryParams.PHPSESSID = authToken;
    }
    let path = [this.routePath, '/poster/upload'];
    let url = this.request.buildUrl(path, queryParams);
    return url;
  }

  async uploadPosterMediaFromUrl(item, mediaUrl) {
    if (!mediaUrl) { return null; }

    let movieId = _.get(item, 'id') || 0;
    let queryParams = { movie_id: movieId, url: mediaUrl };
    let authToken = _.get(this.request, 'config.authToken') || null;
    if (authToken) {
      queryParams.PHPSESSID = authToken;
    }
    let path = [this.routePath, '/poster/upload-url'];
    let res = await this.request.post(path, null, queryParams);
    return res;
  }

  async deletePosterMedia(item) {
    let queryParams = { movie_id: item.id };
    let path = [this.routePath, '/poster/delete'];
    let opts = this._getDefaultRequestOpts();
    let res = await this.request.delete(path, queryParams, opts);
    res = this._populateResponseModel(res);
    return res;
  }

  async getMPAARatings() {
    return _mpaaRatings;
  };

  async getMPAARatingOptions() {
    return _mpaaRatingOptions;
  };

  async getPopularTitles() {
    if (!this._loadPopularTitlesOnce) {
      this._loadPopularTitlesOnce = this._loadPopularTitles();
    }
    return this._loadPopularTitlesOnce;
  }

  async getPopularGenres() {
    if (!this._loadPopularGenresOnce) {
      this._loadPopularGenresOnce = this._loadPopularGenres();
    }
    return this._loadPopularGenresOnce;
  };

  async getPopularCastMembers() {
    if (!this._loadPopularCastMembersOnce) {
      this._loadPopularCastMembersOnce = this._loadPopularCastMembers();
    }
    return this._loadPopularCastMembersOnce;
  };

  async _loadPopularTitles() {
    let path = [this.routePath, 'titles'];
    let opts = this._getDefaultRequestOpts();
    let res = await this.request.get(path, null, opts);
    return _.get(res, 'data') || [];
  }

  async _loadPopularGenres() {
    let path = [this.routePath, 'genres'];
    let opts = this._getDefaultRequestOpts();
    let res = await this.request.get(path, null, opts);
    return _.get(res, 'data') || [];
  }

  async _loadPopularCastMembers() {
    let path = [this.routePath, 'cast'];
    let opts = this._getDefaultRequestOpts();
    let res = await this.request.get(path, null, opts);
    return _.get(res, 'data') || [];
  }

  openImdbTitleWebsite(imdbId) {
    let url = this.getImdbTitleWebsiteUrl(imdbId);
    window.open(url, 'imdb');
  }

  openImdbSearchWebsite(movieTitle = '') {
    let url = this.getImdbSearchWebsiteUrl(movieTitle);
    window.open(url, 'imdb');
  }

  openYoutubeWatchUrl(youtubeCode) {
    let url = this.getYoutubeWatchUrl(youtubeCode);
    window.open(url, 'youtube');
  }

  openYoutubeEmbedUrl(youtubeCode) {
    let url = this.getYoutubeEmbedUrl(youtubeCode);
    window.open(url, 'youtube');
  }

  openYoutubeTrailerSearchWebsite(movieTitle = '') {
    let url = this.getYoutubeTrailerSearchUrl(movieTitle);
    window.open(url, 'youtube');
  }

  getImdbTitleWebsiteUrl(imdbId) {
    let url = this.imdbUrl;
    imdbId = _.trim(imdbId);
    if (!_.isEmpty(imdbId)) {
      url += '/title/' + imdbId + '/';
    }
    return url;
  }

  getImdbSearchWebsiteUrl(movieTitle = '') {
    movieTitle = _.replace(_.trim(movieTitle || ''), /\s+/gi, '+');
    let url = this.imdbUrl + '/find?ref_=nv_sr_fn&s=all&q=' + movieTitle;
    return url;
  }

  getYoutubeTrailerSearchUrl(movieTitle) {
    movieTitle = _.trim(movieTitle || '');
    let searchStr = _.trim(movieTitle + ' official trailer');
    searchStr = searchStr.replace(/\s+/gi, '+');
    let url = this.youtubeUrl + '/results?search_query=' + searchStr;
    return url;
  }

  getYoutubeWatchUrlFromMovie(movie) {
    let url = null;
    if (movie && movie.youtube_code) {
      url = this.youtubeUrl + '/watch?v=' + movie.youtube_code;
    }
    return url;
  }

  getYoutubeEmbedUrlFromMovie(movie) {
    let url = null;
    if (movie && movie.youtube_code) {
      url = this.youtubeUrl + '/embed/' + movie.youtube_code + '?autoplay=1&rel=0&showinfo=0';
    }
    return url;
  }

  getYoutubeWatchUrl(youtubeCode) {
    let url = null;
    if (youtubeCode) {
      youtubeCode = _.trim(youtubeCode || '').replace(/\s+/gi, '+');
      url = this.youtubeUrl + '/watch?v=' + youtubeCode;
    }
    return url;
  }

  getYoutubeEmbedUrl(youtubeCode) {
    let url = null;
    if (youtubeCode) {
      youtubeCode = _.trim(youtubeCode || '').replace(/\s+/gi, '+');
      url = this.youtubeUrl + '/embed/' + youtubeCode + '?autoplay=1&rel=0&showinfo=0';
    }
    return url;
  }

}


